/**
 * @description Public tenant resolve client for Tier-3 custom Host bootstrap
 *
 * Key features:
 * - Same-origin GET /api/public/tenant/resolve (Worker sets X-Original-Host)
 * - In-memory + sessionStorage cache (5 min) keyed by hostname
 * - Fail-closed: 404 / errors → null (caller must not invent an SO)
 *
 * @dependencies
 * - getLynxBuildTenantSlug / isPlatformHostname / usesTenantBffOrigin
 *
 * @notes
 * - Tier-2 *.lynx.build does not call this endpoint (sync slug parse stays authoritative)
 * - Platform hosts (localhost, app.lynxpay.us, first-party *.lynx-dashboard.pages.dev) never call resolve
 */

import {
  getLynxBuildTenantSlug,
  getTenantBackendBaseUrl,
  isPlatformHostname,
  usesTenantBffOrigin,
} from '../../utils/lynxBuildHostname';

const CACHE_PREFIX = 'tenant_resolve_';
/** Match API Cache-Control private, max-age=60 — shrink post-disconnect window. */
const CACHE_TTL_MS = 60 * 1000;

/** @type {Map<string, Promise<object|null>>} */
const inflight = new Map();

/**
 * @typedef {object} ResolvedTenant
 * @property {string} slug
 * @property {string|null} [organizationName]
 * @property {object} [branding]
 * @property {boolean} useBff
 * @property {'dashboard'|'employee'|null} [hostRole]
 * @property {string} [hostname]
 * @property {'lynx-build'|'resolve'} source
 */

/**
 * @param {string} hostname
 * @returns {{ hit: boolean, data: ResolvedTenant|null }}
 */
function readCachedResolve(hostname) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + hostname);
    if (!raw) {
      return { hit: false, data: null };
    }
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_PREFIX + hostname);
      return { hit: false, data: null };
    }
    return { hit: true, data };
  } catch {
    return { hit: false, data: null };
  }
}

/**
 * @param {string} hostname
 * @param {ResolvedTenant|null} data
 */
function writeCachedResolve(hostname, data) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + hostname, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch {
    // ignore quota
  }
}

/**
 * Fetch verified custom-host tenant from public resolve API (same-origin on Tier-3).
 *
 * @param {string} hostname
 * @returns {Promise<ResolvedTenant|null>}
 */
async function fetchCustomHostResolve(hostname) {
  const base = getTenantBackendBaseUrl(hostname);
  const response = await fetch(`${base}/api/public/tenant/resolve`, {
    credentials: 'same-origin',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Tenant resolve failed: ${response.status}`);
  }

  const body = await response.json();
  if (!body?.success || !body?.data?.slug) {
    return null;
  }

  return {
    slug: body.data.slug,
    organizationName: body.data.organizationName || null,
    branding: body.data.branding || {},
    useBff: body.data.useBff !== false,
    hostRole: body.data.hostRole || null,
    hostname: body.data.hostname || hostname,
    source: 'resolve',
  };
}

/**
 * Resolve SO tenant for the current browser host.
 * Tier-2 → sync slug. Platform → null. Custom host → same-origin resolve (404 → null).
 *
 * @param {string} [hostname]
 * @returns {Promise<ResolvedTenant|null>}
 */
export async function resolveTenantForHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  const host = String(hostname || '').toLowerCase();
  if (!host) {
    return null;
  }

  const lynxSlug = getLynxBuildTenantSlug(host);
  if (lynxSlug) {
    return {
      slug: lynxSlug,
      useBff: true,
      source: 'lynx-build',
      hostname: host,
    };
  }

  if (isPlatformHostname(host) || !usesTenantBffOrigin(host)) {
    return null;
  }

  const cached = readCachedResolve(host);
  if (cached.hit) {
    return cached.data;
  }

  if (inflight.has(host)) {
    return inflight.get(host);
  }

  const promise = (async () => {
    try {
      const resolved = await fetchCustomHostResolve(host);
      writeCachedResolve(host, resolved);
      return resolved;
    } catch (error) {
      console.error('Tenant resolve error:', error.message);
      writeCachedResolve(host, null);
      return null;
    } finally {
      inflight.delete(host);
    }
  })();

  inflight.set(host, promise);
  return promise;
}

/**
 * Synchronous slug when already known (Tier-2 parse or session cache from resolve).
 *
 * @param {string} [hostname]
 * @returns {string|null}
 */
export function getCachedTenantSlug(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  const lynxSlug = getLynxBuildTenantSlug(hostname);
  if (lynxSlug) {
    return lynxSlug;
  }
  const cached = readCachedResolve(String(hostname || '').toLowerCase());
  return cached.hit ? (cached.data?.slug || null) : null;
}

/**
 * Clear resolve cache (tests).
 */
export function clearTenantResolveCache() {
  inflight.clear();
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}
